import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { FolderIcon as FolderSolidIcon } from '@heroicons/react/24/solid';
import { useFileStore, type FolderItem as FolderItemType, triggerRefresh } from '../../stores/fileStore';
import { useUIStore } from '../../stores/uiStore';
import { filesApi } from '../../api/files';
import { useToast } from '../ui/Toaster';
import clsx from 'clsx';

interface FolderItemProps {
  folder: FolderItemType;
  viewMode: 'grid' | 'list';
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

export function FolderItem({ folder, viewMode }: FolderItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const { selectedIds, toggleSelection } = useFileStore();
  const openContextMenu = useUIStore((s) => s.openContextMenu);
  const isSelected = selectedIds.includes(folder.id);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('files');

  // Format file count text
  const fileCountText = folder.fileCount !== undefined
    ? folder.fileCount > 0
      ? t('folder.filesCount', { count: folder.fileCount })
      : t('folder.empty')
    : null;

  const handleOpen = () => {
    navigate(`/folder/${folder.id}`);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openContextMenu({ id: folder.id, type: 'folder' }, { x: e.clientX, y: e.clientY });
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const fileIds = e.dataTransfer.getData('application/x-file-ids');
    if (fileIds) {
      setIsDragOver(true);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const fileIdsData = e.dataTransfer.getData('application/x-file-ids');
    if (!fileIdsData) return;

    try {
      const fileIds = JSON.parse(fileIdsData) as string[];
      if (fileIds.length === 0) return;

      await filesApi.bulkAction({
        fileIds,
        action: 'move',
        folderId: folder.id,
      });

      toast.success(
        'Files moved',
        `${fileIds.length} file${fileIds.length > 1 ? 's' : ''} moved to "${folder.name}"`
      );
      triggerRefresh();
    } catch {
      toast.error('Failed to move files');
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        whileTap={{ scale: 0.995 }}
        onClick={() => toggleSelection(folder.id)}
        onDoubleClick={handleOpen}
        onContextMenu={handleContextMenu}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={clsx(
          'flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800',
          isSelected && 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500',
          isDragOver && 'bg-primary-100 dark:bg-primary-900/50 ring-2 ring-primary-500 ring-dashed'
        )}
      >
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: folder.color ? `${folder.color}20` : undefined }}
        >
          <FolderSolidIcon
            className="w-6 h-6"
            style={{ color: folder.color || '#6b7280' }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {fileCountText ?? t('folder.empty')} • {formatDate(folder.updatedAt)}
          </p>
        </div>

        {isDragOver && (
          <span className="text-primary-600 dark:text-primary-400 text-sm font-medium">
            Drop here
          </span>
        )}
      </motion.div>
    );
  }

  // Grid view - diseño compacto horizontal
  return (
    <motion.div
      whileHover={{ scale: isDragOver ? 1.03 : 1.01 }}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => toggleSelection(folder.id)}
      onDoubleClick={handleOpen}
      onContextMenu={handleContextMenu}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        'relative flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all',
        isSelected
          ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500 shadow-md'
          : 'bg-white dark:bg-slate-800 hover:shadow-md border border-gray-100 dark:border-slate-700',
        isDragOver && 'bg-primary-100 dark:bg-primary-900/50 ring-2 ring-primary-500'
      )}
    >
      {/* Hover checkbox - en la esquina derecha para no tapar el icono */}
      {isHovered && !isDragOver && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-2 right-2 z-10"
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelection(folder.id)}
            onClick={(e) => e.stopPropagation()}
            className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 dark:bg-slate-700"
          />
        </motion.div>
      )}

      {/* Folder icon */}
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: folder.color ? `${folder.color}15` : 'rgba(107, 114, 128, 0.1)' }}
      >
        <FolderSolidIcon
          className={clsx('w-6 h-6 transition-transform', isDragOver && 'scale-110')}
          style={{ color: folder.color || '#6366f1' }}
        />
      </div>

      {/* Folder name */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 dark:text-white truncate text-sm" title={folder.name}>
          {folder.name}
        </p>
        <p className="text-xs text-gray-500 dark:text-slate-400">
          {isDragOver ? 'Drop here' : (fileCountText ?? t('folder.empty'))}
        </p>
      </div>
    </motion.div>
  );
}
